import React, { useState, useEffect } from "react";
import "./App.css";
import { Toaster } from "sonner";
import { DealProvider, useDeal } from "./lib/store";
import { verdict } from "./lib/model";
import Dashboard from "./pages/Dashboard";
import Firm from "./pages/Firm";
import AIEvaluation from "./pages/AIEvaluation";
import Assumptions from "./pages/Assumptions";
import Returns from "./pages/Returns";
import SourcesUses from "./pages/SourcesUses";
import ProfitLoss from "./pages/ProfitLoss";
import Sensitivity from "./pages/Sensitivity";
import AIAssistant from "./pages/AIAssistant";
import TeaserGenerator from "./pages/TeaserGenerator";
import Investors from "./pages/Investors";
import Lenders from "./pages/Lenders";
import BrokerLOI from "./pages/BrokerLOI";
import DealScout from "./pages/DealScout";
import ClosedDeals from "./pages/ClosedDeals";
import Pipeline from "./pages/Pipeline";
import RollUp from "./pages/RollUp";
import NNNBuyers from "./pages/NNNBuyers";
import PlacementAgents from "./pages/PlacementAgents";
import ExportMemo from "./pages/ExportMemo";
import BorrowingBase from "./pages/BorrowingBase";
import DealRoom from "./pages/DealRoom";
import Portfolio from "./pages/Portfolio";
import CapitalMarkets from "./pages/CapitalMarkets";
import OutreachInbox from "./pages/OutreachInbox";
import OffMarket from "./pages/OffMarket";
import OwnerList from "./pages/OwnerList";
import BizDev from "./pages/BizDev";
import MyDeals from "./pages/MyDeals";
import DocsRoom from "./pages/DocsRoom";
import Intelligence from "./pages/Intelligence";
import { allowedTabs, canSee, MEMBER_HOME } from "./lib/access";
import {
  LayoutGrid, CheckSquare, SlidersHorizontal, TrendingUp, Grid3x3,
  MessageSquare, Upload, FileText, Users, ScrollText, Columns3, Download, MoreHorizontal, Building2, Landmark, Radar, History, Layers, Home, Handshake, LogOut, UsersRound, Wallet, FileCheck2, Globe, Plus, Briefcase, PiggyBank, FolderOpen, Inbox, Crosshair, BarChart3, Sparkles,
} from "lucide-react";
import { AuthProvider, useAuth } from "./lib/auth";
import Login from "./pages/Login";
import { TeamModal } from "./components/TeamModal";
import InvestorRoom from "./InvestorRoom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ThemeToggle } from "./components/ThemeToggle";
import { useTheme } from "./lib/theme";

export const PAGES = [
  { id: "dashboard", label: "Dashboard", icon: LayoutGrid, C: Dashboard },
  { id: "firm", label: "Firm Profile", icon: Building2, C: Firm },
  { id: "portfolio", label: "Portfolio", icon: Briefcase, C: Portfolio },
  { id: "evaluation", label: "AI Evaluation", icon: CheckSquare, C: AIEvaluation },
  { id: "assumptions", label: "Assumptions", icon: SlidersHorizontal, C: Assumptions },
  { id: "borrowingbase", label: "Borrowing Base", icon: FileCheck2, C: BorrowingBase },
  { id: "returns", label: "Returns", icon: TrendingUp, C: Returns },
  { id: "sourcesuses", label: "Sources & Uses", icon: Wallet, C: SourcesUses },
  { id: "profitloss", label: "Profit & Loss", icon: BarChart3, C: ProfitLoss },
  { id: "sensitivity", label: "Sensitivity", icon: Grid3x3, C: Sensitivity },
  { id: "assistant", label: "AI Assistant", icon: MessageSquare, C: AIAssistant },
  { id: "teaser", label: "Teaser Generator", icon: FileText, C: TeaserGenerator },
  { id: "docs", label: "Docs Room", icon: FolderOpen, C: DocsRoom },
  { id: "intel", label: "Intelligence", icon: Sparkles, C: Intelligence },
  { id: "scout", label: "Deal Scout", icon: Radar, C: DealScout },
  { id: "offmarket", label: "Off-Market Targets", icon: Crosshair, C: OffMarket },
  { id: "owners", label: "Owner List", icon: UsersRound, C: OwnerList },
  { id: "bizdev", label: "Business Dev", icon: TrendingUp, C: BizDev },
  { id: "closed", label: "Closed Deals", icon: History, C: ClosedDeals },
  { id: "investors", label: "Investors", icon: Users, C: Investors },
  { id: "capital", label: "Capital Markets", icon: PiggyBank, C: CapitalMarkets },
  { id: "inbox", label: "Outreach Inbox", icon: Inbox, C: OutreachInbox },
  { id: "lenders", label: "Debt Lenders", icon: Landmark, C: Lenders },
  { id: "placement", label: "Placement Agents", icon: Handshake, C: PlacementAgents },
  { id: "loi", label: "Broker Outreach", icon: ScrollText, C: BrokerLOI },
  { id: "pipeline", label: "Pipeline", icon: Columns3, C: Pipeline },
  { id: "rollup", label: "Roll-Up", icon: Layers, C: RollUp },
  { id: "nnn", label: "NNN Buyers", icon: Home, C: NNNBuyers },
  { id: "export", label: "CIM Builder", icon: Download, C: ExportMemo },
  { id: "dealroom", label: "Deal Room", icon: Globe, C: DealRoom },
];

const MOBILE_MAIN = ["dashboard", "evaluation", "assumptions", "returns"];
const DRAWER_ITEMS = ["firm", "portfolio", "borrowingbase", "sourcesuses", "profitloss", "sensitivity", "assistant", "teaser", "docs", "intel", "scout", "offmarket", "owners", "bizdev", "closed", "investors", "capital", "inbox", "lenders", "placement", "rollup", "nnn", "loi", "pipeline", "export", "dealroom"];

function Shell() {
  const { M, active, navigate, saveState, startNewDeal, dealName } = useDeal();
  const { user, logout } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const [team, setTeam] = useState(false);
  const isAdmin = !user || user.role === "admin" || user.access?.admin;
  const allowed = allowedTabs(user, M, dealName);
  const pages = isAdmin ? PAGES : [{ id: MEMBER_HOME, label: "My Deals", icon: FolderOpen, C: null }, ...PAGES.filter((p) => canSee(allowed, p.id))];
  useEffect(() => {
    if (!isAdmin && !pages.some((p) => p.id === active)) navigate(MEMBER_HOME);
  }, [isAdmin, active, allowed?.join("|")]); // eslint-disable-line
  const v = verdict(M);
  const dot = v === "GO" ? "go" : v === "PASS" ? "pass" : "amber";
  // Mount each page on first visit and KEEP it mounted (hidden when inactive) so
  // in-flight AI requests + their results survive tab switches — the backend fetch
  // keeps streaming and lands in the still-mounted component instead of being lost.
  const [visited, setVisited] = useState(() => new Set([active]));
  useEffect(() => {
    setVisited((s) => (s.has(active) ? s : new Set(s).add(active)));
  }, [active]);
  const go = (id) => { navigate(id); setDrawer(false); };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-logo">XYZ VENTURES</div>
          <div className="brand-sub">Underwriting Terminal</div>
        </div>
        <div className="deal-chip" data-testid="deal-chip">
          <div className="name"><span className={`pulse-dot ${dot}`} />{M.dealName}</div>
          <div className="meta">{[M.industry, M.address].filter(Boolean).join(" · ")}</div>
          <div className={`save-state ${saveState}`} data-testid="save-state">
            {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved to pipeline" : saveState === "offline" ? "Offline — will sync" : saveState === "error" ? "Save failed — retrying" : "Unsaved changes"}
          </div>
        </div>
        {isAdmin && <button className="btn btn-primary new-deal-btn" onClick={startNewDeal} data-testid="new-deal-btn"><Plus size={13} style={{ marginRight: 6 }} />Start New Deal</button>}
        <nav className="nav">
          {pages.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.id} className={`nav-item ${active === p.id ? "active" : ""}`} onClick={() => go(p.id)} data-testid={`nav-${p.id}`}>
                <span className="idx">{i + 1}</span>
                <Icon size={15} />
                <span>{p.label}</span>
              </div>
            );
          })}
        </nav>
        <div className="sb-theme"><ThemeToggle testid="sidebar-theme" /></div>
        <div className="side-user" data-testid="side-user">
          <div className="su-info">
            <div className="su-name">{user?.name || user?.email}</div>
            <div className="su-email">{user?.email}</div>
          </div>
          <div className="su-actions">
            {isAdmin && <button className="su-btn" onClick={() => setTeam(true)} title="Manage team access" data-testid="open-team"><UsersRound size={14} />Team</button>}
            <button className="su-btn" onClick={logout} title="Sign out" data-testid="logout-btn"><LogOut size={14} />Sign out</button>
          </div>
        </div>
      </aside>

      <div className="mobile-topbar" data-testid="mobile-topbar">
        <div className="mt-brand">XYZ VENTURES</div>
        <div className="mt-deal"><span className={`pulse-dot ${dot}`} />{M.dealName} · {v}</div>
      </div>

      <main className="main">
        {!isAdmin && active === MEMBER_HOME && <MyDeals pages={PAGES} onOpen={(tabs) => navigate(tabs ? (tabs[0] || MEMBER_HOME) : "dashboard")} />}
        {pages.filter((p) => p.C && visited.has(p.id)).map((p) => {
          const C = p.C;
          return (
            <div key={p.id} className={`page-swap${active === p.id ? " on" : ""}`} style={{ display: active === p.id ? "block" : "none" }} data-testid={`page-wrap-${p.id}`}>
              <C />
            </div>
          );
        })}
      </main>

      {/* mobile bottom tabs */}
      <nav className="mobile-nav">
        {(isAdmin ? MOBILE_MAIN : pages.slice(0, 4).map((p) => p.id)).map((id) => {
          const p = pages.find((x) => x.id === id); const Icon = p.icon;
          return (
            <div key={id} className={`mtab ${active === id ? "active" : ""}`} onClick={() => go(id)} data-testid={`mtab-${id}`}>
              <Icon size={18} /><span>{p.label.split(" ")[0]}</span>
            </div>
          );
        })}
        <div className={`mtab ${drawer ? "active" : ""}`} onClick={() => setDrawer(true)} data-testid="mtab-more">
          <MoreHorizontal size={18} /><span>More</span>
        </div>
      </nav>

      {drawer && <div className="drawer-overlay" onClick={() => setDrawer(false)} />}
      {drawer && (
        <div className="drawer" data-testid="drawer">
          <div className="dh">More</div>
          {(isAdmin ? DRAWER_ITEMS : pages.slice(4).map((p) => p.id)).map((id) => {
            const p = pages.find((x) => x.id === id); const Icon = p.icon;
            return <div key={id} className="di" onClick={() => go(id)} data-testid={`drawer-${id}`}><Icon size={18} />{p.label}</div>;
          })}
          {isAdmin && <div className="di" onClick={() => { setDrawer(false); setTeam(true); }} data-testid="drawer-team"><UsersRound size={18} />Team access</div>}
          <div className="di" onClick={logout} data-testid="drawer-logout"><LogOut size={18} />Sign out</div>
        </div>
      )}
      <TeamModal open={team} onClose={() => setTeam(false)} />
    </div>
  );
}

function Gate() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="auth-loading" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Login />;
  return (
    <DealProvider>
      <Shell />
    </DealProvider>
  );
}

export default function App() {
  return <ErrorBoundary><AppInner /></ErrorBoundary>;
}

function AppInner() {
  const { resolved } = useTheme();
  const room = window.location.pathname.match(/^\/room\/([A-Za-z0-9_-]+)/);
  if (room) {
    return (
      <>
        <InvestorRoom shareId={room[1]} />
        <Toaster theme={resolved} position="top-right" />
      </>
    );
  }
  return (
    <AuthProvider>
      <Gate />
      <Toaster theme={resolved} position="top-right" />
    </AuthProvider>
  );
}
